// Copyright 2022 - 2023 Fewcha. All rights reserved.

import { SpinnerIos } from "@styled-icons/fluentui-system-regular";
import { useAppSelector } from "app/hooks";
import { selectTheme } from "features/theme/themeReducer";
import React, { PropsWithChildren } from "react";
import cn from "services/cn";

const Button: React.FC<
  PropsWithChildren<{
    onClick?: () => void;
    className?: string;
    disabled?: boolean;
    hide?: boolean;
    type?: "button" | "submit" | "reset";
    kind?: "normal" | "success" | "confirm" | "cancel" | "error";
    loading?: boolean;
    size?: "large" | "normal" | "small";
    block?: boolean;
  }>
> = ({
  children,
  onClick,
  className,
  disabled,
  hide,
  type = "button",
  loading,
  kind = "normal",
  size = "normal",
  block,
}) => {
  const theme = useAppSelector(selectTheme);

  if (hide) {
    return null;
  }

  return (
    <button
      formNoValidate
      type={type}
      className={cn(
        "fewcha--button select-none cursor-pointer rounded-[77px] text-[14px] font-medium",

        {
          "py-2 px-5": size === "normal",
          "py-1 px-2": size === "small",
          "py-3 px-10 text-[16px]": size === "large",

          "color-dark": kind === "normal" && !disabled,
          [`color-${theme.theme}-button`]: kind === "normal" && !disabled,
          [`color-${theme.theme}-button-error`]: kind === "error" && !disabled,
          [`color-${theme.theme} color-${theme.theme}-bg-surface-lower color-${theme.theme}-border border`]:
            kind === "cancel",
          [`!bg-[#2C3135] !bg-none !text-[#6F757C] !shadow-none !transition-none`]: loading || disabled,
          block,

          "w-full": block,
        },

        className,
      )}
      onClick={onClick}
      disabled={loading || disabled}
    >
      <div
        className={cn("flex items-center justify-center", {
          "gap-2": loading,
        })}
      >
        {loading && <SpinnerIos className="animate-spin" size={25} />}
        {children}
      </div>
    </button>
  );
};

export default Button;
